import { Camera, Check, ChevronLeft, ChevronRight, ImagePlus, Loader2, Trash2, Upload } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppShell } from "../components/app-shell";
import { ResponsiveImage } from "../components/responsive-image";
import { createApiProject } from "../lib/api";
import { createDemoProject } from "../lib/demo";
import { projectSchema, validateImageBytes } from "../lib/schemas";
import { saveProject } from "../lib/storage";
import { exteriorSpaces, interiorSpaces, styles, type ProjectKind } from "../lib/types";

type FormValues = {
  kind: ProjectKind;
  space: string;
  style: (typeof styles)[number];
  budgetMin: number;
  budgetMax: number;
  colours: string[];
  keep: string;
  remove: string;
  notes: string;
  wallWidthCm?: number | "";
};

type ProjectDraft = {
  step: number;
  preview?: string;
  values: Partial<FormValues>;
};

const draftKey = "prostorai:project-draft";

function loadDraft(): ProjectDraft | null {
  try {
    const raw = sessionStorage.getItem(draftKey);
    return raw ? (JSON.parse(raw) as ProjectDraft) : null;
  } catch {
    return null;
  }
}

const steps = ["Prostor", "Vrsta", "Fotografija", "Preferencije", "Potvrda"];
const colours = [
  { value: "pijesak", label: "Pijesak", color: "#ddd1c1" },
  { value: "maslinasta", label: "Maslinasta", color: "#71806d" },
  { value: "bijela", label: "Bijela", color: "#f7f4ed" },
  { value: "terakota", label: "Terakota", color: "#bf6d4c" },
  { value: "antracit", label: "Antracit", color: "#343b37" },
  { value: "plava", label: "Jadranska", color: "#567b82" },
];

async function downscaleImage(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  const scale = Math.min(1, 2400 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Preglednik ne može obraditi ovu fotografiju.");
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return canvas.toDataURL("image/webp", 0.78);
}

export function NewProjectPage() {
  const [searchParams] = useSearchParams();
  const savedDraft = useMemo(loadDraft, []);
  const kindParam = searchParams.get("kind");
  const requestedKind =
    kindParam === "interior" || kindParam === "exterior"
      ? kindParam
      : (savedDraft?.values.kind ?? "interior");
  const [step, setStep] = useState(() => Math.min(4, Math.max(0, savedDraft?.step ?? 0)));
  const [preview, setPreview] = useState<string | undefined>(savedDraft?.preview);
  const [fileError, setFileError] = useState<string>();
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [processingImage, setProcessingImage] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { register, watch, setValue, handleSubmit, getValues } = useForm<FormValues>({
    defaultValues: {
      space: "",
      style: "Mediteranski",
      budgetMin: 2_000,
      budgetMax: 8_000,
      colours: ["pijesak", "maslinasta"],
      keep: "",
      remove: "",
      notes: "",
      wallWidthCm: "",
      ...savedDraft?.values,
      kind: requestedKind,
    },
  });
  const values = watch();
  const spaces = values.kind === "interior" ? interiorSpaces : exteriorSpaces;

  useEffect(() => {
    if (values.space && !(spaces as readonly string[]).includes(values.space)) setValue("space", "");
  }, [setValue, spaces, values.space]);

  useEffect(() => {
    const persist = (current: Partial<FormValues>) => {
      try {
        sessionStorage.setItem(draftKey, JSON.stringify({ step, preview, values: current }));
      } catch {
        sessionStorage.setItem(draftKey, JSON.stringify({ step, values: current }));
      }
    };
    persist(getValues());
    const subscription = watch((current) => persist(current));
    return () => subscription.unsubscribe();
  }, [getValues, preview, step, watch]);

  const stepReady = useMemo(() => {
    if (step === 0) return Boolean(values.kind);
    if (step === 1) return Boolean(values.space);
    if (step === 2) return Boolean(preview);
    return true;
  }, [preview, step, values.kind, values.space]);

  const onFile = async (file?: File) => {
    if (!file) return;
    setFileError(undefined);
    setProcessingImage(true);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const error = validateImageBytes(bytes, file.type);
      if (error) {
        setFileError(error);
        return;
      }
      setPreview(await downscaleImage(file));
    } catch {
      setFileError("Fotografiju nije moguće obraditi. Pokušaj s drugom datotekom.");
    } finally {
      setProcessingImage(false);
    }
  };

  const next = () => {
    if (!stepReady) {
      setFormErrors([
        step === 1
          ? "Odaberi vrstu prostora prije nastavka."
          : "Dodaj fotografiju prostora ili upotrijebi demo fotografiju.",
      ]);
      return;
    }
    setFormErrors([]);
    setStep((value) => Math.min(4, value + 1));
  };

  const createProject = async (data: FormValues) => {
    const parsed = projectSchema.safeParse(data);
    if (!parsed.success) {
      setFormErrors(parsed.error.issues.map((issue) => issue.message));
      setStep(3);
      return;
    }
    const normalized = parsed.data;
    const project = createDemoProject(normalized.kind, normalized.space);
    project.preferences = {
      style: normalized.style,
      budgetMin: normalized.budgetMin,
      budgetMax: normalized.budgetMax,
      colours: normalized.colours,
      keep: normalized.keep,
      remove: normalized.remove,
      notes: normalized.notes,
      wallWidthCm: normalized.wallWidthCm,
    };
    project.originalImageUrl =
      preview ??
      (normalized.kind === "interior"
        ? "/images/interior-before-1024.webp"
        : "/images/exterior-after-1024.webp");
    project.status = "queued";
    setSubmitting(true);
    try {
      const remote = await createApiProject(
        project.kind,
        project.space,
        project.preferences,
        project.originalImageUrl,
      );
      if (remote) {
        project.id = remote.id;
        project.expiresAt = remote.expiresAt;
        project.backend = "worker";
      } else {
        project.backend = "local";
      }
    } catch (error) {
      setFormErrors([error instanceof Error ? error.message : "Projekt nije moguće pokrenuti."]);
      setSubmitting(false);
      return;
    }
    try {
      saveProject(project);
    } catch {
      project.originalImageUrl =
        data.kind === "interior" ? "/images/interior-before-1024.webp" : "/images/exterior-after-1024.webp";
      saveProject(project);
    }
    sessionStorage.removeItem(draftKey);
    void navigate(`/app/projects/${project.id}/generating`);
  };

  return (
    <AppShell eyebrow="Novi projekt" backTo="/" backLabel="Na početnu">
      <div className="wizard-heading">
        <div>
          <p className="step-count">
            Korak {step + 1} od {steps.length}
          </p>
          <h1>{steps[step]}</h1>
        </div>
        <ol className="stepper" aria-label="Napredak projekta">
          {steps.map((label, index) => (
            <li key={label} className={index === step ? "is-current" : index < step ? "is-complete" : ""}>
              <span>{index < step ? <Check aria-hidden="true" /> : index + 1}</span>
              <small>{label}</small>
            </li>
          ))}
        </ol>
      </div>

      {formErrors.length > 0 && (
        <div className="error-summary" role="alert" tabIndex={-1}>
          <strong>Provjeri prije nastavka:</strong>
          <ul>
            {formErrors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <form className="wizard" onSubmit={(event) => void handleSubmit(createProject)(event)}>
        {step === 0 && (
          <fieldset className="choice-grid choice-grid--kind">
            <legend>Što želiš urediti?</legend>
            <label className={`image-choice ${values.kind === "interior" ? "is-selected" : ""}`}>
              <input type="radio" value="interior" {...register("kind")} />
              <ResponsiveImage name="interior-after" alt="" sizes="(max-width: 700px) 100vw, 42vw" />
              <span>
                <strong>Interijer</strong>
                <small>Dnevni boravak, kuhinja, spavaća soba i još pet vrsta prostora.</small>
              </span>
            </label>
            <label className={`image-choice ${values.kind === "exterior" ? "is-selected" : ""}`}>
              <input type="radio" value="exterior" {...register("kind")} />
              <ResponsiveImage name="exterior-after" alt="" sizes="(max-width: 700px) 100vw, 42vw" />
              <span>
                <strong>Eksterijer</strong>
                <small>Fasada, terasa, balkon, vrt, ograda, ulaz i krov.</small>
              </span>
            </label>
          </fieldset>
        )}

        {step === 1 && (
          <fieldset className="space-choice">
            <legend>Odaberi vrstu prostora</legend>
            <p>To nam pomaže prepoznati elemente i ponuditi smislenije proizvode.</p>
            <div className="option-grid">
              {spaces.map((space) => (
                <label key={space} className={values.space === space ? "is-selected" : ""}>
                  <input type="radio" value={space} {...register("space")} />
                  <span>{space}</span>
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {step === 2 && (
          <section className="upload-step" aria-labelledby="upload-title">
            <div className="upload-step__copy">
              <h2 id="upload-title">Dodaj jasnu fotografiju cijelog prostora.</h2>
              <ul>
                <li>Snimaj ravno, bez jakog zooma.</li>
                <li>Uključi pod, zidove, prozore i vrata.</li>
                <li>JPG, PNG ili WebP do 12 MB.</li>
              </ul>
              <p className="privacy-note">
                Fotografiju možeš ukloniti odmah; automatski se briše nakon 7 dana.
              </p>
            </div>
            <div
              className={`upload-zone ${fileError ? "has-error" : ""}`}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                void onFile(event.dataTransfer.files[0]);
              }}
            >
              {preview ? (
                <div className="upload-preview">
                  <img src={preview} alt="Pregled odabrane fotografije prostora" />
                  <button
                    className="button button--light upload-remove"
                    type="button"
                    onClick={() => setPreview(undefined)}
                  >
                    <Trash2 aria-hidden="true" /> Ukloni fotografiju
                  </button>
                </div>
              ) : (
                <>
                  <span className="upload-zone__icon">
                    {processingImage ? (
                      <Loader2 className="spin" aria-hidden="true" />
                    ) : (
                      <ImagePlus aria-hidden="true" />
                    )}
                  </span>
                  <strong>{processingImage ? "Pripremamo fotografiju…" : "Povuci fotografiju ovdje"}</strong>
                  <span>ili</span>
                  <button
                    className="button button--primary"
                    type="button"
                    onClick={() => fileInput.current?.click()}
                  >
                    <Upload aria-hidden="true" /> Odaberi datoteku
                  </button>
                  <button
                    className="text-button"
                    type="button"
                    onClick={() => {
                      setPreview(
                        values.kind === "interior"
                          ? "/images/interior-before-1024.webp"
                          : "/images/exterior-after-1024.webp",
                      );
                    }}
                  >
                    Upotrijebi demo fotografiju
                  </button>
                  <input
                    ref={fileInput}
                    className="sr-only"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    capture="environment"
                    onChange={(event) => void onFile(event.target.files?.[0])}
                  />
                </>
              )}
              {fileError && <p className="field-error">{fileError}</p>}
            </div>
          </section>
        )}

        {step === 3 && (
          <section className="preferences" aria-labelledby="preferences-title">
            <div className="preferences__intro">
              <h2 id="preferences-title">Daj prijedlozima praktičan okvir.</h2>
              <p>Ne moraš ispuniti sve, ali stil, boje i budžet najviše utječu na rezultat i matching.</p>
            </div>
            <div className="form-grid">
              <label className="field field--full">
                <span>Stil</span>
                <select {...register("style")}>
                  {styles.map((style) => (
                    <option key={style}>{style}</option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Budžet od (€)</span>
                <input type="number" min="0" step="100" {...register("budgetMin", { valueAsNumber: true })} />
              </label>
              <label className="field">
                <span>Budžet do (€)</span>
                <input
                  type="number"
                  min="100"
                  step="100"
                  {...register("budgetMax", { valueAsNumber: true })}
                />
              </label>
              <fieldset className="colour-field field--full">
                <legend>Boje</legend>
                <div className="colour-options">
                  {colours.map((colour) => (
                    <label key={colour.value}>
                      <input type="checkbox" value={colour.value} {...register("colours")} />
                      <span style={{ "--swatch": colour.color } as React.CSSProperties}>{colour.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <label className="field">
                <span>Što želiš zadržati?</span>
                <textarea placeholder="npr. parket, prozore i postojeću sofu" {...register("keep")} />
              </label>
              <label className="field">
                <span>Što želiš ukloniti?</span>
                <textarea placeholder="npr. TV komodu i tamne zavjese" {...register("remove")} />
              </label>
              <label className="field field--full">
                <span>Opiši željeni rezultat</span>
                <textarea
                  placeholder="Želim topliji prostor s više mjesta za sjedenje, ali bez zatrpanih polica."
                  {...register("notes")}
                />
              </label>
              <label className="field">
                <span>Širina glavnog zida (cm, opcionalno)</span>
                <input
                  type="number"
                  min="30"
                  max="5000"
                  {...register("wallWidthCm", { valueAsNumber: true })}
                />
              </label>
            </div>
          </section>
        )}

        {step === 4 && (
          <section className="confirmation" aria-labelledby="confirmation-title">
            <div>
              <p className="section-label">Spremno za pet prijedloga</p>
              <h2 id="confirmation-title">{values.space}</h2>
              <dl className="confirmation__summary">
                <div>
                  <dt>Vrsta</dt>
                  <dd>{values.kind === "interior" ? "Interijer" : "Eksterijer"}</dd>
                </div>
                <div>
                  <dt>Stil</dt>
                  <dd>{values.style}</dd>
                </div>
                <div>
                  <dt>Budžet</dt>
                  <dd>
                    {values.budgetMin.toLocaleString("hr-HR")}–{values.budgetMax.toLocaleString("hr-HR")} €
                  </dd>
                </div>
                <div>
                  <dt>Boje</dt>
                  <dd>{values.colours.join(", ")}</dd>
                </div>
              </dl>
              <p className="confirmation__legal">
                Nastavkom potvrđuješ da smiješ koristiti fotografiju. Rezultat je vizualni prijedlog, ne
                arhitektonska ni građevinska procjena.
              </p>
            </div>
            <div className="confirmation__image">
              <img
                src={
                  preview ??
                  (values.kind === "interior"
                    ? "/images/interior-before-1024.webp"
                    : "/images/exterior-after-1024.webp")
                }
                alt="Fotografija projekta"
              />
              <span>
                <Camera aria-hidden="true" /> Fotografija projekta
              </span>
            </div>
          </section>
        )}

        <div className="wizard-actions">
          {step > 0 ? (
            <button
              className="button button--ghost"
              type="button"
              onClick={() => setStep((value) => value - 1)}
            >
              <ChevronLeft aria-hidden="true" /> Natrag
            </button>
          ) : (
            <span />
          )}
          {step < 4 ? (
            <button
              key="next-step"
              className="button button--primary"
              type="button"
              onClick={(event) => {
                event.preventDefault();
                next();
              }}
            >
              Nastavi <ChevronRight aria-hidden="true" />
            </button>
          ) : (
            <button
              key="submit-project"
              className="button button--primary"
              type="submit"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="spin" aria-hidden="true" /> Sigurno spremamo…
                </>
              ) : (
                <>
                  Stvori pet prijedloga <ChevronRight aria-hidden="true" />
                </>
              )}
            </button>
          )}
        </div>
      </form>
    </AppShell>
  );
}
